import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListEventsQueryKey,
  useListEvents,
  useListStudents,
  useCreateEvent,
  useDeleteEvent,
  type Event as EventRecord,
  type Student
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { CalendarDaysIcon, PlusIcon, ArrowRightIcon, CheckIcon, ArrowUpIcon, ArrowDownIcon, TrashIcon, RepeatIcon, AlertTriangleIcon } from 'lucide-react';

/** Return today's local date as a `YYYY-MM-DD` string (avoids UTC off-by-one). */
function todayLocalISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Whether a `YYYY-MM-DD` date string is today or in the future (date-only comparison). */
function isTodayOrFuture(date: string): boolean {
  return date >= todayLocalISO();
}

/** Format a "HH:MM" time-of-day string for display (e.g. "12:30 am"). */
function formatTimeOfDay(time: string): string {
  const parts = time.split(':');
  const h = Number(parts[0] ?? 0);
  const m = Number(parts[1] ?? 0);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

/** Format an optional start/end time range, or return null if neither is set. */
function formatTimeRange(start: string | null, end: string | null): string | null {
  if (!start && !end) return null;
  if (start && end) return `${formatTimeOfDay(start)} – ${formatTimeOfDay(end)}`;
  if (start) return `from ${formatTimeOfDay(start)}`;
  return `until ${formatTimeOfDay(end!)}`;
}

type SortColumn = 'name' | 'date' | 'time' | 'location' | 'status';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

/** Compare two events for the given column. Returns negative when `a` precedes `b`. */
function compareEvents(a: EventRecord, b: EventRecord, column: SortColumn): number {
  switch (column) {
    case 'date': {
      const d = a.date.localeCompare(b.date);
      if (d !== 0) return d;
      if (a.startTime && b.startTime) {
        const t = a.startTime.localeCompare(b.startTime);
        if (t !== 0) return t;
      } else if (a.startTime && !b.startTime) {
        return -1;
      } else if (!a.startTime && b.startTime) {
        return 1;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    }
    case 'time': {
      if (a.startTime && b.startTime) {
        const t = a.startTime.localeCompare(b.startTime);
        if (t !== 0) return t;
      } else if (a.startTime && !b.startTime) {
        return -1;
      } else if (!a.startTime && b.startTime) {
        return 1;
      }
      return a.date.localeCompare(b.date);
    }
    case 'name':
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    case 'location':
      return a.location.localeCompare(b.location, undefined, { sensitivity: 'base' });
    case 'status':
      if (a.status === b.status) return 0;
      return a.status === 'active' ? -1 : 1;
  }
}

function SortableHead({ label, column, sortState, onSort }: {
  label: string;
  column: SortColumn;
  sortState: SortState;
  onSort: (col: SortColumn) => void;
}) {
  const isActive = sortState.column === column;
  return (
    <TableHead aria-sort={isActive ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {label}
        {isActive && sortState.direction === 'asc' && <ArrowUpIcon className="size-3.5" />}
        {isActive && sortState.direction === 'desc' && <ArrowDownIcon className="size-3.5" />}
      </button>
    </TableHead>
  );
}

export function EventsPage() {
  const { data, isLoading } = useListEvents();
  const events = data?.events ?? [];
  const [showAll, setShowAll] = React.useState(false);
  const [sortState, setSortState] = React.useState<SortState>({ column: 'date', direction: 'asc' });

  const handleSort = React.useCallback((column: SortColumn) => {
    setSortState((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  }, []);

  const visibleEvents = React.useMemo(
    () => (showAll ? events : events.filter((e) => isTodayOrFuture(e.date))),
    [events, showAll]
  );

  const sortedEvents = React.useMemo(() => {
    const sorted = [...visibleEvents];
    sorted.sort((a, b) => {
      const cmp = compareEvents(a, b, sortState.column);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [visibleEvents, sortState]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="School Events"
        description="Create offsite events and manage student attendance"
        icon={CalendarDaysIcon}
        actions={<CreateEventDialog />}
      />
      <Card className="overflow-hidden border-border/80 bg-card py-0 shadow-sm shadow-primary/5">
        <div className="flex items-center justify-between gap-4 border-b border-border/80 px-4 py-3">
          <div className="flex items-center gap-3">
            <Switch
              id="show-past-events"
              checked={showAll}
              onCheckedChange={setShowAll}
              aria-label="Show past events"
            />
            <Label htmlFor="show-past-events" className="text-sm font-medium cursor-pointer">
              Show past events
            </Label>
          </div>
          {!showAll && events.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {events.length - visibleEvents.length} past event{events.length - visibleEvents.length === 1 ? '' : 's'} hidden
            </span>
          )}
        </div>
        <Table className="[&_thead]:bg-muted/70 [&_thead_th]:h-12 [&_thead_th]:px-4 [&_thead_th]:text-xs [&_thead_th]:font-semibold [&_thead_th]:uppercase [&_thead_th]:tracking-wider [&_tbody_td]:px-4 [&_tbody_td]:py-4">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableHead label="Event Name" column="name" sortState={sortState} onSort={handleSort} />
              <SortableHead label="Date" column="date" sortState={sortState} onSort={handleSort} />
              <SortableHead label="Time" column="time" sortState={sortState} onSort={handleSort} />
              <SortableHead label="Location" column="location" sortState={sortState} onSort={handleSort} />
              <SortableHead label="Status" column="status" sortState={sortState} onSort={handleSort} />
              <TableHead className="w-36 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  Loading events…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  No events yet. Create one to get started.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && events.length > 0 && visibleEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  No upcoming events. Toggle “Show past events” to see earlier sessions.
                </TableCell>
              </TableRow>
            )}
            {sortedEvents.map((e: EventRecord) => (
              <TableRow key={e.id} className="group hover:bg-primary/5 focus-within:bg-primary/5">
                <TableCell className="font-semibold">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/events/${e.id}`}
                      className="underline-offset-4 transition-colors hover:text-primary hover:underline"
                    >
                      {e.name}
                    </Link>
                    {e.seriesId && (
                      <Badge variant="outline" className="gap-1 border-primary/20 text-xs text-primary">
                        <RepeatIcon className="size-3" />
                        Series
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(e.date).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatTimeRange(e.startTime, e.endTime) ?? <span className="text-muted-foreground/50">—</span>}
                </TableCell>
                <TableCell className="text-muted-foreground">{e.location}</TableCell>
                <TableCell>
                  <Badge
                    className={
                      e.status === 'active'
                        ? 'border-success/20 bg-success/10 text-success'
                        : 'border-border bg-muted text-muted-foreground'
                    }
                  >
                    {e.status === 'active' ? 'Active' : 'Completed'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
                    >
                      <Link to={`/events/${e.id}`}>
                        Open
                        <ArrowRightIcon className="size-3.5" />
                      </Link>
                    </Button>
                    <DeleteEventButton eventId={e.id} eventName={e.name} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function DeleteEventButton({ eventId, eventName }: { eventId: string; eventName: string }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const mutation = useDeleteEvent();

  const handleDelete = async () => {
    await mutation.mutateAsync({ id: eventId });
    await queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-destructive"
          aria-label={`Delete ${eventName}`}
        >
          <TrashIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangleIcon className="size-5 text-destructive" />
            Delete event
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <span className="font-semibold text-foreground">{eventName}</span>?
            All attendance records and roll calls for this session will be permanently removed. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={mutation.isPending}>
            {mutation.isPending ? 'Deleting…' : 'Delete event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateEventDialog() {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const mutation = useCreateEvent();
  const { data: studentsData } = useListStudents();
  const allStudents = studentsData?.students ?? [];

  const [form, setForm] = React.useState({ name: '', date: '', location: '', startTime: '', endTime: '' });
  const [recurring, setRecurring] = React.useState(false);
  const [recurrenceCount, setRecurrenceCount] = React.useState(5);
  const [selectedStudents, setSelectedStudents] = React.useState<Set<string>>(new Set());
  const [rollCallNames, setRollCallNames] = React.useState<string[]>(['Roll call']);

  const toggleStudent = (id: string) => {
    const next = new Set(selectedStudents);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedStudents(next);
  };

  const grouped = React.useMemo(() => {
    const map = new Map<string, Student[]>();
    for (const s of allStudents) {
      const group = s.group;
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(s);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [allStudents]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mutation.mutateAsync({
      data: {
        name: form.name,
        date: form.date,
        location: form.location,
        studentIds: [...selectedStudents],
        rollCalls: rollCallNames.filter((n) => n.trim().length > 0),
        ...(form.startTime ? { startTime: form.startTime } : {}),
        ...(form.endTime ? { endTime: form.endTime } : {}),
        ...(recurring ? { recurrence: { frequency: 'weekly' as const, count: recurrenceCount } } : {})
      }
    });
    await queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() });
    setForm({ name: '', date: '', location: '', startTime: '', endTime: '' });
    setRecurring(false);
    setRecurrenceCount(5);
    setSelectedStudents(new Set());
    setRollCallNames(['Roll call']);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 size-4" />
          Create Event
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
            <DialogDescription>
              Set up an offsite event with optional session times and weekly recurrence, then check in the attending students.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="e-name">Event Name</Label>
              <Input id="e-name" placeholder="District Athletics Carnival" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="e-date">Date</Label>
                <Input id="e-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="e-loc">Location</Label>
                <Input id="e-loc" placeholder="Civic Park" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="e-start">Start time</Label>
                <Input id="e-start" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="e-end">End time</Label>
                <Input id="e-end" type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
              </div>
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Repeats weekly</p>
                  <p className="text-xs text-muted-foreground">Create multiple occurrences 7 days apart, sharing the same details and roster.</p>
                </div>
                <Switch checked={recurring} onCheckedChange={setRecurring} aria-label="Toggle weekly recurrence" />
              </div>
              {recurring && (
                <div className="mt-3 grid gap-2">
                  <Label htmlFor="e-count">Number of weeks</Label>
                  <Input
                    id="e-count"
                    type="number"
                    min={1}
                    max={52}
                    value={recurrenceCount}
                    onChange={(e) => setRecurrenceCount(Math.min(52, Math.max(1, Number(e.target.value) || 1)))}
                  />
                </div>
              )}
            </div>
            <div className="border-t pt-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold">Roll calls</p>
                <Badge className="border-info/20 bg-info/10 text-info">
                  {rollCallNames.filter((n) => n.trim()).length} planned
                </Badge>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Define the checkpoints for this event (e.g. Leaving school, On bus, Arrived at venue). Defaults to a single roll call.
              </p>
              <div className="space-y-2">
                {rollCallNames.map((name, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-6 shrink-0 text-center text-xs font-semibold text-muted-foreground tabular-nums">{idx + 1}</span>
                    <Input
                      value={name}
                      onChange={(e) => {
                        const next = [...rollCallNames];
                        next[idx] = e.target.value;
                        setRollCallNames(next);
                      }}
                      placeholder={`Roll call ${idx + 1}`}
                      className="flex-1"
                    />
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={idx === 0}
                        onClick={() => {
                          const next = [...rollCallNames];
                          [next[idx - 1], next[idx]] = [next[idx]!, next[idx - 1]!];
                          setRollCallNames(next);
                        }}
                      >
                        <ArrowUpIcon className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={idx === rollCallNames.length - 1}
                        onClick={() => {
                          const next = [...rollCallNames];
                          [next[idx + 1], next[idx]] = [next[idx]!, next[idx + 1]!];
                          setRollCallNames(next);
                        }}
                      >
                        <ArrowDownIcon className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive hover:text-destructive"
                        disabled={rollCallNames.length <= 1}
                        onClick={() => {
                          setRollCallNames(rollCallNames.filter((_, i) => i !== idx));
                        }}
                      >
                        <TrashIcon className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 gap-1"
                onClick={() => setRollCallNames([...rollCallNames, ''])}
              >
                <PlusIcon className="size-3.5" />
                Add roll call
              </Button>
            </div>
            <div className="border-t pt-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold">Check in students</p>
                <Badge className="border-info/20 bg-info/10 text-info">
                  {selectedStudents.size} selected
                </Badge>
              </div>
              <div className="space-y-4">
                {grouped.map(([group, students]) => (
                  <div key={group}>
                    <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">{group}</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {students.map((s) => {
                        const selected = selectedStudents.has(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => toggleStudent(s.id)}
                            aria-pressed={selected}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm font-medium shadow-xs transition-all focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/20 focus-visible:outline-none ${
                              selected
                                ? 'border-primary bg-primary/10 text-primary shadow-sm shadow-primary/10'
                                : 'border-border bg-card hover:border-primary/35 hover:bg-accent/60'
                            }`}
                          >
                            <div className={`flex size-4 shrink-0 items-center justify-center rounded-sm border ${selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background'}`}>
                              {selected && <CheckIcon className="size-3" />}
                            </div>
                            {s.firstName} {s.lastInitial}.
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {allStudents.length === 0 && (
                  <p className="text-muted-foreground py-2 text-sm">
                    No students available. Add students first.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending || !form.name || !form.date || !form.location || rollCallNames.filter((n) => n.trim()).length === 0}>
              {mutation.isPending ? 'Creating…' : 'Create Event'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
